import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { isCreativeId, useLocationParams } from 'hooks';
import {
  fetchCreativesWithUrlIds,
  getCreativesWithHypothesis,
  normalizeCreativeParams,
  queryKeys as creativesQueryKeys,
} from 'features/creatives';
import type { CreativesWithHypothesisParams } from 'features/creatives';
import { MarketingProcessCode, Network } from '../../../constants';
import { SINGLE_AD_TYPE } from '../constants';
import { collectUrlCreativeIds } from '../utils/deepLinkInit';
import { buildPacksFromCreativesByPack } from '../utils/buildPacksFromCreativesByPack';
import {
  getCreativesFormatMismatchMessage,
  isCreativesFormatCompatible,
  isScalingEgProcess,
} from '../utils/marketingProcess';
import type { DeepLinkStepResult } from './deepLinkTypes';
import type { StepInitStatus } from 'components/layout';

export interface DeepLinkCreativesStepResult extends DeepLinkStepResult {
  mismatchMessage?: string;
}

const resolveCreativesStepStatus = ({
  creativesCompatible,
  needsCreativeIdCheck,
  needsCreativeListFetch,
  isPending,
  isError,
  hasMissing,
}: {
  creativesCompatible: boolean;
  needsCreativeIdCheck: boolean;
  needsCreativeListFetch: boolean;
  isPending: boolean;
  isError: boolean;
  hasMissing: boolean;
}): StepInitStatus => {
  if (!creativesCompatible) return 'failed';
  if (!needsCreativeIdCheck) return 'success';
  if (!needsCreativeListFetch) return hasMissing ? 'failed' : 'success';
  if (isPending) return 'loading';
  if (isError || hasMissing) return 'failed';
  return 'success';
};

const useDeepLinkCreativesStep = (): DeepLinkCreativesStepResult => {
  const { setValue } = useFormContext();
  const formApp = useWatch({ name: 'app' }) as string | undefined;
  const {
    app: appParam,
    adType: adTypeParam,
    marketingProcess: marketingProcessParam,
    creativesByPack,
    flatCreatives,
    creativesFormat,
    visualTouchUps: visualTouchUpsParam,
    filterCreativesByName: filterCreativesByNameParam,
  } = useLocationParams();

  const resolvedApp = appParam ?? formApp;
  const isScalingEgParam = isScalingEgProcess(marketingProcessParam);
  const isHypothesisTestingParam =
    marketingProcessParam === MarketingProcessCode.HYPOTHESIS_TESTING;
  const resolvedAdType = isHypothesisTestingParam
    ? SINGLE_AD_TYPE
    : (adTypeParam ?? '');

  const creativesCompatible = isCreativesFormatCompatible(
    marketingProcessParam,
    creativesFormat
  );
  const mismatchMessage = creativesCompatible
    ? undefined
    : getCreativesFormatMismatchMessage(marketingProcessParam);

  const urlCreativeIds = useMemo(
    () =>
      collectUrlCreativeIds(creativesFormat, creativesByPack, flatCreatives),
    [creativesByPack, creativesFormat, flatCreatives]
  );
  const invalidCreativeIds = useMemo(
    () => urlCreativeIds.filter((id) => !isCreativeId(id)),
    [urlCreativeIds]
  );
  const uniqueCreativeIds = useMemo(
    () => urlCreativeIds.filter((id) => isCreativeId(id)),
    [urlCreativeIds]
  );
  const needsCreativeIdCheck = creativesCompatible && urlCreativeIds.length > 0;
  const needsCreativeListFetch =
    creativesCompatible && uniqueCreativeIds.length > 0;
  const creativesRequestParams = useMemo(
    () =>
      normalizeCreativeParams({
        app: resolvedApp ?? undefined,
        network: Network.FACEBOOK,
        ...(resolvedAdType ? { ad_type: resolvedAdType } : {}),
        ...(filterCreativesByNameParam.length
          ? { query: filterCreativesByNameParam }
          : {}),
      }),
    [filterCreativesByNameParam, resolvedAdType, resolvedApp],
  );

  const hypothesisCreativesRequestParams = useMemo(
    (): CreativesWithHypothesisParams => ({
      app: resolvedApp ?? undefined,
      network: Network.FACEBOOK,
      ...(resolvedAdType ? { ad_type: resolvedAdType } : {}),
    }),
    [resolvedAdType, resolvedApp],
  );

  const creativesQuery = useQuery({
    queryKey: creativesQueryKeys.creatives(creativesRequestParams),
    queryFn: ({ signal }) =>
      fetchCreativesWithUrlIds(
        {
          params: creativesRequestParams,
          urlCreativeIds: uniqueCreativeIds,
        },
        signal,
      ),
    enabled: needsCreativeListFetch && !isHypothesisTestingParam,
    staleTime: 0,
    refetchOnMount: false,
  });

  const hypothesisCreativesQuery = useQuery({
    queryKey: creativesQueryKeys.creativesWithHypothesis(
      hypothesisCreativesRequestParams,
    ),
    queryFn: ({ signal }) =>
      getCreativesWithHypothesis(
        { params: hypothesisCreativesRequestParams },
        signal,
      ),
    enabled: needsCreativeListFetch && isHypothesisTestingParam,
    staleTime: 0,
  });

  const knownCreativeIds = useMemo(() => {
    if (isHypothesisTestingParam) {
      if (!hypothesisCreativesQuery.data) return undefined;
      return new Set(
        Object.values(hypothesisCreativesQuery.data).flatMap((rows) =>
          rows.map((row) => String(row.id))
        )
      );
    }
    if (!creativesQuery.data) return undefined;
    return new Set(creativesQuery.data.map((row) => String(row.id)));
  }, [
    creativesQuery.data,
    hypothesisCreativesQuery.data,
    isHypothesisTestingParam,
  ]);

  const missing = useMemo(() => {
    const missingFromList = knownCreativeIds
      ? uniqueCreativeIds.filter((id) => !knownCreativeIds.has(id))
      : [];
    return [...invalidCreativeIds, ...missingFromList];
  }, [invalidCreativeIds, knownCreativeIds, uniqueCreativeIds]);

  const activeQuery = isHypothesisTestingParam
    ? hypothesisCreativesQuery
    : creativesQuery;
  const status = resolveCreativesStepStatus({
    creativesCompatible,
    needsCreativeIdCheck,
    needsCreativeListFetch,
    isPending: activeQuery.isPending,
    isError: activeQuery.isError,
    hasMissing: missing.length > 0,
  });

  const restore = useCallback(() => {
    if (!needsCreativeIdCheck) return;

    if (isScalingEgParam) {
      setValue('selectedCreatives', uniqueCreativeIds);
      setValue('filterCreativesByName', [...filterCreativesByNameParam], {
        shouldValidate: true,
      });
      return;
    }

    const knownIds = new Set(uniqueCreativeIds);
    const packs = buildPacksFromCreativesByPack(
      creativesByPack,
      resolvedAdType,
      visualTouchUpsParam
    ).map((pack, index) => ({
      ...pack,
      creatives: pack.creatives.filter(
        (id): id is string => typeof id === 'string' && knownIds.has(id)
      ),
      ...(index === 0
        ? { filterCreativeByName: [...filterCreativesByNameParam] }
        : {}),
    }));

    if (packs.length) {
      setValue('packsWithCreatives', packs);
      setValue('activePacksWithCreatives', 0);
    }
    if (!isHypothesisTestingParam) {
      setValue(
        'selectedCreatives',
        (packs[0]?.creatives ?? []).filter(
          (id): id is string => typeof id === 'string'
        )
      );
    }
    if (filterCreativesByNameParam.length) {
      setValue('filterCreativesByName', [...filterCreativesByNameParam], {
        shouldValidate: true,
      });
    }
  }, [
    creativesByPack,
    filterCreativesByNameParam,
    isHypothesisTestingParam,
    isScalingEgParam,
    needsCreativeIdCheck,
    resolvedAdType,
    setValue,
    uniqueCreativeIds,
    visualTouchUpsParam,
  ]);

  return {
    status,
    pass: status === 'success',
    missing,
    mismatchMessage,
    restore,
  };
};

export default useDeepLinkCreativesStep;
